import React, { useState } from 'react';
import EmailSubmit from './EmailSubmit';
import { sendConfiguration } from '../service/handleSubmit';

/**
 * EmailCard Component
 * 
 * Renders a modal card that allows users to enter their email address and submit it 
 * along with configuration details. The card also provides a close button.
 * 
 * @param {Object} props - The component props
 * @param {Array} props.selectedModules - List of selected modules to include in the email
 * @param {Function} props.setShowEmailCard - Function to show or hide the email card
 * @param {string} props.deliveryTime - Delivery time information to include in the email
 * @param {number} props.price - Price information to include in the email
 * @returns {JSX.Element} - The rendered email card component
 */
const EmailCard = ({ selectedModules, setShowEmailCard, deliveryTime, price }) => {
    const [email, setEmail] = useState(""); // State for storing the email input

    /**
     * Handles email submission by validating the email and sending the configuration.
     */
    const handleEmailSubmit = async () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!email) {
            alert('Please enter an email address.');
            return;
        }

        if (!emailRegex.test(email)) {
            alert('Please enter a valid email address.');
            return;
        }

        try {
            const response = await sendConfiguration(email, selectedModules, deliveryTime, price); // Sends configuration details
            if (response.status === 'success') {
                alert('Configuration sent successfully!');
                handleCloseCard(); // Closes the card after successful submission
            } else {
                console.error("Failed to send configuration:", response);
                alert('Failed to send configuration.');
            }
        } catch (error) {
            alert('Error sending configuration.');
            console.error('Error:', error);
        }
    };

    /**
     * Closes the email card.
     */
    const handleCloseCard = () => {
        setShowEmailCard(false);
    };

    return (
        <div style={{
            position: 'fixed', // Fixes the position of the card in the viewport
            top: '50%', // Centers the card vertically
            left: '50%', // Centers the card horizontally
            transform: 'translate(-50%, -50%)', // Centers the card exactly
            width: '400px', // Sets a fixed width for the card
            padding: '30px', // Adds padding inside the card
            borderRadius: '8px', // Rounds the corners of the card
            backgroundColor: '#fff', // Sets the background color of the card
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)', // Adds a subtle shadow around the card
            zIndex: 1000, // Ensures the card is on top of other content
        }}>
            <h3 style={{ fontFamily: 'Open Sans', textAlign: 'center', color: '#000' }}>
                Receive Documentation
            </h3>
            <EmailSubmit
                email={email}
                setEmail={setEmail}
                onSubmit={handleEmailSubmit} // Handles email submission
                buttonStyle={{
                    textTransform: 'none', // Disables uppercase transformation on the button text
                    margin: '10px', // Adds margin around the button
                    padding: '15px 40px', // Adds padding inside the button
                    textAlign: 'center', // Centers the text inside the button
                    transition: '0.5s', // Adds a smooth transition effect on hover
                    backgroundSize: '200% auto', // Scales the background gradient
                    color: 'white', // Sets the text color
                    borderRadius: '10px', // Rounds the corners of the button
                    display: 'block', // Makes the button a block element
                    border: '0px', // Removes the border of the button
                    fontWeight: 700, // Sets the font weight
                    boxShadow: '0px 0px 14px -7px #2b8cb9', // Adds a shadow to the button
                    backgroundImage: 'linear-gradient(45deg, #064D6F 0%, #2b8cb9 51%, #1BB2F8 100%)', // Sets a gradient background
                    cursor: 'pointer', // Changes the cursor to a pointer on hover
                    userSelect: 'none' // Disables text selection
                }}
            />
            <button
                onClick={handleCloseCard}
                style={{
                    position: 'relative', // Allows positioning relative to the card
                    right: '2%', // Adjusts the position to the right
                    textTransform: 'none', // Disables uppercase transformation on the button text
                    margin: '10px', // Adds margin around the button
                    padding: '12px 40px', // Adds padding inside the button
                    textAlign: 'center', // Centers the text inside the button
                    transition: '0.5s', // Adds a smooth transition effect on hover
                    backgroundSize: '200% auto', // Scales the background gradient
                    color: 'white', // Sets the text color
                    borderRadius: '10px', // Rounds the corners of the button
                    display: 'block', // Makes the button a block element
                    border: '0px', // Removes the border of the button
                    fontWeight: 700, // Sets the font weight
                    boxShadow: '0px 0px 14px -7px #2b8cb9', // Adds a shadow to the button
                    backgroundImage: 'linear-gradient(45deg, #064D6F 0%, #2b8cb9 51%, #1BB2F8 100%)', // Sets a gradient background
                    cursor: 'pointer', // Changes the cursor to a pointer on hover
                    userSelect: 'none' // Disables text selection
                }}
            >
                Close
            </button>
        </div>
    );
};

export default EmailCard;
